'use client';

import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';

/* ══════════════════════════════════════════════════════════════════════════
   HelpContactDialog – Wiederverwendbarer Kontakt-Dialog
   Zeigt Wahlmöglichkeit: Technische Hilfe / Inhaltliche Hilfe
   Wird anstelle von mailto:-Links eingesetzt
   ══════════════════════════════════════════════════════════════════════════ */

interface HelpContactDialogProps {
  /** Trigger-Element – rendert children als klickbares Element */
  children: React.ReactNode;
  /** Optional: CSS-Klassen für den Trigger-Wrapper */
  className?: string;
}

export function HelpContactDialog({ children, className }: HelpContactDialogProps) {
  const t = useTranslations('helpDialog');
  const [isOpen, setIsOpen] = useState(false);
  const [helpType, setHelpType] = useState<'technical' | 'content' | null>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  // Reset on close
  useEffect(() => {
    if (!isOpen) setHelpType(null);
  }, [isOpen]);

  return (
    <>
      {/* Trigger */}
      <span
        role="button"
        tabIndex={0}
        onClick={() => setIsOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsOpen(true);
          }
        }}
        className={className}
      >
        {children}
      </span>

      {/* Modal Overlay – via Portal, damit es über der gesamten Seite liegt */}
      {isOpen && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => {
            // Klick auf Backdrop (nicht auf Dialog) → schließen
            if (e.target === e.currentTarget) setIsOpen(false);
          }}
        >
          <div
            ref={dialogRef}
            className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 animate-in zoom-in-95 duration-200"
          >
            {/* Close Button */}
            <button
              onClick={() => setIsOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>

            {/* Header */}
            <h3 className="text-xl font-bold text-gray-900 mb-2">{t('title')}</h3>
            <p className="text-sm text-gray-600 mb-6">{t('description')}</p>

            {/* Help Type Selection */}
            <div className="flex flex-col gap-3 mb-6">
              <button
                onClick={() => setHelpType('technical')}
                className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 transition-all ${
                  helpType === 'technical'
                    ? 'border-teal-600 bg-teal-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-teal-300 hover:bg-gray-50'
                }`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                  helpType === 'technical' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-500'
                }`}>
                  <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className={`font-semibold text-sm ${helpType === 'technical' ? 'text-teal-700' : 'text-gray-900'}`}>{t('technicalHelp')}</p>
                  <p className="text-xs text-gray-500">{t('technicalHelpDesc')}</p>
                </div>
              </button>

              <button
                onClick={() => setHelpType('content')}
                className={`flex items-center gap-3 px-4 py-3.5 rounded-xl border-2 transition-all ${
                  helpType === 'content'
                    ? 'border-teal-600 bg-teal-50 shadow-sm'
                    : 'border-gray-200 bg-white hover:border-teal-300 hover:bg-gray-50'
                }`}
              >
                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${
                  helpType === 'content' ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-500'
                }`}>
                  <svg className="w-4.5 h-4.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className={`font-semibold text-sm ${helpType === 'content' ? 'text-teal-700' : 'text-gray-900'}`}>{t('contentHelp')}</p>
                  <p className="text-xs text-gray-500">{t('contentHelpDesc')}</p>
                </div>
              </button>
            </div>

            {/* Action Button */}
            {helpType ? (
              <a
                href={
                  helpType === 'technical'
                    ? 'mailto:ak@code-craft.tech?cc=anaskarah996@gmail.com,remington@medizzconnect.com&subject=Technische%20Hilfe%20-%20medizzconnect'
                    : 'mailto:info@medizzconnect.com?subject=Inhaltliche%20Hilfe%20-%20medizzconnect'
                }
                onClick={() => setIsOpen(false)}
                className="flex items-center justify-center w-full bg-teal-600 text-white font-semibold px-6 py-3 rounded-xl hover:bg-teal-700 transition-colors"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                {helpType === 'technical' ? t('technicalHelpButton') : t('contentHelpButton')}
              </a>
            ) : (
              <p className="text-sm text-gray-400 text-center">{t('selectCategory')}</p>
            )}
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
